import { useNavigate } from 'react-router-dom'
import { Clock, FileText, Unlock, Info, ListChecks } from 'lucide-react'
import PageContainer from '@/components/layout/PageContainer'
import StickySideBanner from '@/components/common/StickySideBanner'
import Button from '@/components/ui/Button'
import Card from '@/components/ui/Card'
import SEO from '@/components/common/SEO'
import { useCareCalculatorStore } from '@/stores/careCalculatorStore'
import { useCalcQuestions } from '@/features/carecalc/api'
import { cn } from '@/lib/utils'
import { trackStartCareCalculator } from '@/lib/analytics'
import CareCalcFaq from '@/components/care-calculator/CareCalcFaq'

/**
 * 돌봄지원 계산기 시작 화면.
 *
 * 면책을 숨기지 않고 시작 화면에 고정 노출한다. 「통합돌봄」이라는 단어는
 * 진입점에 쓰지 않는다 — 시행 5개월차라 국민 42.9%가 시행 사실을 모른다.
 */
export default function CareCalculatorStartPage() {
  const navigate = useNavigate()
  const { mode, setMode, answers, step, reset } = useCareCalculatorStore()
  const { data: questions } = useCalcQuestions(mode)

  const answeredCount = Object.keys(answers).length
  const hasProgress = answeredCount > 0 && !useCareCalculatorStore.getState().completed

  const start = () => {
    trackStartCareCalculator(mode)
    reset()
    navigate('/care-calculator/profile')
  }

  const resume = () => navigate('/care-calculator/step')

  return (
    <>
      {/* 좌우 세로배너 160x600 — 콘텐츠 max-w-2xl. 336+160+48(여백) = 544 */}
      <StickySideBanner slotKey="carecalc_side_left" side="left" topPx={96} edgeOffsetPx={544} breakpoint="xl" />
      <StickySideBanner slotKey="carecalc_side_right" side="right" topPx={96} edgeOffsetPx={544} breakpoint="xl" />
      {/* 「통합돌봄」은 2026-03 시행이라 아직 검색 수요가 거의 없다. 타이틀 앞자리를
          검색되지 않는 단어에 쓰지 않는다. 화면·메뉴 이름은 그대로 두고 검색 타이틀만 바꾼다.
          「자가진단」은 /guide/self-assessment 가 쓰고 있어 겹치지 않게 피한다.
          기획: docs/plan/2026-08-25-carecalc-seo-plan.md */}
      <SEO
        title="케어밸런스 요양등급 계산기 - 예상 등급과 요양원 한 달 비용"
        description="3분이면 부모님 예상 장기요양등급과 한 달 본인부담금을 알 수 있어요. 공단 인정조사 52개 항목 기준으로 계산하고, 받으실 수 있는 돌봄 서비스도 함께 알려드립니다."
        url="/care-calculator"
        brandSuffix={false}
      />

      {/* 기본 상한 480px 은 모바일 기준이라 PC 에서 화면 가운데로 쪼그라든다.
          체크리스트와 같은 max-w-2xl 로 맞춰 좌우 배너 자리를 만든다 */}
      <PageContainer noMaxWidth>
        <div className="mx-auto flex max-w-2xl flex-col gap-6 py-4">
          <div>
            <h1 className="text-h1 font-bold leading-snug text-cb-gray-900">
              부모님 돌봄,
              <br />어떻게 준비하면 좋을까요?
            </h1>
            <p className="mt-3 text-body leading-relaxed text-cb-gray-600">
              부모님 상태를 여쭤보고, 집에서 모실 때와 요양원에 모실 때 드는 비용을 비교해 드려요.
              어떤 돌봄 서비스를 지원받으실 수 있는지도 함께 알려드립니다.
            </p>
          </div>

          {/* 모드 선택 — 52항목 전부는 10분이 걸려 이탈이 크다 */}
          <div className="flex flex-col gap-2.5">
            <ModeCard
              selected={mode === 'QUICK'}
              onSelect={() => setMode('QUICK')}
              title="빠른 진단"
              time="약 3분"
              desc="핵심 항목만 여쭤봐요. 대략의 등급과 비용을 먼저 보고 싶을 때."
            />
            <ModeCard
              selected={mode === 'FULL'}
              onSelect={() => setMode('FULL')}
              title="정밀 진단"
              time="약 10분"
              desc="세부 항목까지 하나하나 여쭤봐요. 더 정확한 결과가 필요할 때."
              recommended
            />
          </div>

          <Card>
            <ul className="flex flex-col gap-3 text-body-sm text-cb-gray-700">
              <li className="flex items-center gap-2.5">
                <Clock size={18} className="flex-shrink-0 text-cb-primary" />
                {mode === 'QUICK' ? '약 3분이면 끝나요' : '약 10분이면 끝나요'}
              </li>
              <li className="flex items-center gap-2.5">
                <FileText size={18} className="flex-shrink-0 text-cb-primary" />
                {questions ? `${questions.totalItems}개 항목을 여쭤봐요` : '상태를 여쭤봐요'}
              </li>
              <li className="flex items-center gap-2.5">
                <ListChecks size={18} className="flex-shrink-0 text-cb-primary" />
                받으실 수 있는 돌봄 서비스 목록도 같이 보여드려요
              </li>
              <li className="flex items-center gap-2.5">
                <Unlock size={18} className="flex-shrink-0 text-cb-primary" />
                로그인 없이 결과까지 보실 수 있어요
              </li>
            </ul>
          </Card>

          {/* 면책 — 숨기지 않고 시작 화면에 둔다 */}
          <div className="flex gap-2.5 rounded-xl bg-amber-50 p-4">
            <Info size={18} className="mt-0.5 flex-shrink-0 text-amber-700" />
            <p className="text-body-sm leading-relaxed text-amber-900">
              여기서 나오는 결과는 답변만으로 계산한 예상치예요. 실제 등급은 국민건강보험공단이
              방문조사와 의사 소견서를 거쳐 정합니다.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            {hasProgress ? (
              <>
                <Button size="lg" fullWidth onClick={resume}>
                  이어서 하기 ({answeredCount}개 답하셨어요)
                </Button>
                <Button size="lg" variant="secondary" fullWidth onClick={start}>
                  처음부터 다시
                </Button>
              </>
            ) : (
              <Button size="lg" fullWidth onClick={start}>
                시작하기
              </Button>
            )}
            <button
              type="button"
              onClick={() => navigate('/cost-simulator')}
              className="mt-1 text-body-sm font-medium text-cb-primary-active underline"
            >
              이미 등급을 받으셨나요? 비용만 바로 계산하기
            </button>
          </div>

          {/* 시작 버튼 아래에 둔다. 위에 두면 읽다가 시작을 미루게 된다 */}
          <CareCalcFaq />

          {/* step 은 이어하기 판단에만 쓰이므로 화면에 노출하지 않는다 */}
          <span className="sr-only">{step}</span>
        </div>
      </PageContainer>
    </>
  )
}

/** 진단 모드 선택 카드 */
function ModeCard({
  selected,
  onSelect,
  title,
  time,
  desc,
  recommended,
}: {
  selected: boolean
  onSelect: () => void
  title: string
  time: string
  desc: string
  recommended?: boolean
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'rounded-2xl border-2 px-4 py-4 text-left transition-colors',
        selected
          ? 'border-cb-primary bg-cb-primary-light'
          : 'border-cb-gray-200 bg-white hover:border-cb-gray-300',
      )}
    >
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold',
            selected ? 'border-cb-primary bg-cb-primary text-white' : 'border-cb-gray-300 text-transparent',
          )}
          aria-hidden="true"
        >
          ✓
        </span>
        <span
          className={cn(
            'text-[1.0625rem] font-bold',
            selected ? 'text-cb-primary' : 'text-cb-gray-900',
          )}
        >
          {title}
        </span>
        <span className="text-body-sm text-cb-gray-600">{time}</span>
        {recommended && (
          <span className="rounded-full bg-cb-secondary/10 px-2 py-0.5 text-caption font-medium text-cb-secondary">
            더 정확해요
          </span>
        )}
      </div>
      <p className="mt-1.5 pl-7 text-[0.9375rem] leading-relaxed text-cb-gray-600">{desc}</p>
    </button>
  )
}
